/**
 * (wizard-v2, owner choice "B" 2026-11-04) Setup wizard defaults per country + the Getting-started checklist items.
 * Shared by the API (source of truth: what the wizard writes, what the checklist ticks) and the web app (labels, links).
 */
import { taxRegimeOf } from "./us-uk.js";

/** the seven wizard steps in order; only profile, branch and review (Go live) are required */
export const WIZARD_STEPS = ["profile", "branch", "services", "team", "checkout", "bookings", "review"] as const;
export type WizardStep = (typeof WIZARD_STEPS)[number];
export const WIZARD_STEP_COUNT = WIZARD_STEPS.length;

// ------------------------------------------------------------------------------------------------ country tax card
export type TaxCardKind = "my" | "sg" | "au" | "gb" | "us" | "other";
export interface TaxCard {
  kind: TaxCardKind;
  /** label + rate suggested for the country (ppm, 80000 = 8 %); 0 = no preset (owner enters it) */
  label: string; ratePpm: number; inclusive: boolean;
  /** UK: "I'm not VAT-registered → 0 %"; elsewhere a generic "I don't charge {label}" switch */
  registrationSwitch: boolean;
}
/** MY SST 8 % added · SG GST 9 % included · AU GST 10 % included · UK VAT 20 % included · US combined sales tax added (rate entered) */
export function countryTaxCard(country: string | null | undefined): TaxCard {
  const c = (country ?? "").trim().toUpperCase();
  const regime = taxRegimeOf(c);
  if (regime === "us") return { kind: "us", label: "Sales tax", ratePpm: 0, inclusive: false, registrationSwitch: false };
  if (regime === "gb") return { kind: "gb", label: "VAT", ratePpm: 200_000, inclusive: true, registrationSwitch: true };
  if (c === "MY") return { kind: "my", label: "SST", ratePpm: 80_000, inclusive: false, registrationSwitch: true };
  if (c === "SG") return { kind: "sg", label: "GST", ratePpm: 90_000, inclusive: true, registrationSwitch: true };
  if (c === "AU") return { kind: "au", label: "GST", ratePpm: 100_000, inclusive: true, registrationSwitch: true };
  return { kind: "other", label: "Tax", ratePpm: 0, inclusive: true, registrationSwitch: false };
}

// ------------------------------------------------------------------------------------------------ payment types
export interface PaySuggestion {
  /** stable key: a built-in method (cash / external_card / bank_transfer) or "custom:<slug>" for a shop method */
  key: string;
  builtin: "cash" | "external_card" | "bank_transfer" | null;
  name: string; nameZh: string;
  icon: "wallet" | "qr" | "phone" | "card" | "bank" | "coins";
  /** ticked by default in the wizard */
  ticked: boolean;
  requiresRef?: boolean;
}
const b = (builtin: "cash" | "external_card" | "bank_transfer", ticked = true, name?: string, nameZh?: string): PaySuggestion => ({
  key: builtin, builtin, ticked, icon: builtin === "cash" ? "coins" : builtin === "external_card" ? "card" : "bank",
  name: name ?? (builtin === "cash" ? "Cash" : builtin === "external_card" ? "Card terminal" : "Bank transfer"),
  nameZh: nameZh ?? (builtin === "cash" ? "现金" : builtin === "external_card" ? "刷卡机" : "银行转账"),
});
const cu = (slug: string, name: string, nameZh: string, icon: PaySuggestion["icon"], ticked = true): PaySuggestion => ({ key: "custom:" + slug, builtin: null, name, nameZh, icon, ticked });

/** Payment types suggested at the Checkout step (country suggestions; everything stays editable in Settings → Checkout). */
export function suggestedPayMethods(country: string | null | undefined): PaySuggestion[] {
  const c = (country ?? "").trim().toUpperCase();
  const regime = taxRegimeOf(c);
  if (c === "MY") return [b("cash"), b("external_card"), cu("duitnow", "DuitNow QR", "DuitNow QR", "qr"), cu("tng", "Touch 'n Go eWallet", "Touch 'n Go 电子钱包", "phone"), cu("grabpay", "GrabPay", "GrabPay", "phone"), b("bank_transfer")];
  if (c === "SG") return [b("cash"), b("external_card"), cu("paynow", "PayNow", "PayNow", "qr"), cu("nets", "NETS", "NETS", "card"), b("bank_transfer", false)];
  if (c === "AU") return [b("cash"), b("external_card", true, "EFTPOS / card terminal", "EFTPOS / 刷卡机"), b("bank_transfer", false)];
  if (regime === "gb") return [b("external_card", true, "Card", "刷卡"), b("cash"), b("bank_transfer", false)];
  if (regime === "us") return [b("external_card", true, "Card", "刷卡"), b("cash"), cu("cashapp", "Cash App", "Cash App", "phone", false), cu("venmo", "Venmo", "Venmo", "phone", false), cu("zelle", "Zelle", "Zelle", "bank", false)];
  return [b("cash"), b("external_card"), b("bank_transfer")];
}

/** Tips at checkout: on by default for US shops, suggested (unticked, highlighted) for the UK, off elsewhere. */
export function tipsDefault(country: string | null | undefined): "on" | "suggest" | "off" {
  const regime = taxRegimeOf((country ?? "").trim().toUpperCase());
  return regime === "us" ? "on" : regime === "gb" ? "suggest" : "off";
}

/** Receipt footer prefilled from the location's address + phone. */
export function receiptFooterFrom(address: string | null | undefined, phone: string | null | undefined): string {
  return [address?.replace(/\s*\n\s*/g, ", ").trim(), phone?.trim()].filter((x) => !!x).join(" · ").slice(0, 500);
}

// ------------------------------------------------------------------------------------------------ getting-started checklist
export type ChecklistGroup = "essentials" | "grow" | "full";
// The item definitions (keys + owner-portal hrefs) live server-side in apps/api/src/checklist.ts so no /owner/ link ever
// ends up in the store-app bundle (check-native-bundle); the web gets each item's href from GET /api/setup/checklist.

export interface ChecklistItem { key: string; group: ChecklistGroup; href: string; done: boolean; manual: boolean; locked: boolean }
export interface ChecklistView {
  items: ChecklistItem[];
  done: number; total: number;
  /** owner closed it (per tenant) */
  dismissed: boolean;
  /** shops that went live before the checklist existed see it collapsed */
  collapsed: boolean;
}
